<script setup lang="ts">
import type { SEO, Category, Post } from '~/types';

const { params, path } = useRoute();
const { data: globals } = await useGlobals();
const { fileUrl } = useFiles();

// Helper to normalize Directus file relation (accepts id or file object)
function safeFileId(fileOrId: unknown): string | undefined {
  if (!fileOrId) return undefined;
  if (typeof fileOrId === 'string') return fileOrId;

  // Directus file object may have an `id` property
  if (typeof fileOrId === 'object' && fileOrId !== null && 'id' in (fileOrId as any)) {
    return (fileOrId as any).id as string | undefined;
  }

  return undefined;
}

const { data } = await useAsyncData(
  path,
  () => {
    const categoryPromise = useDirectusProxy<Category[]>({
      operation: 'readItems',
      collection: 'categories',
      params: {
        filter: {
          slug: {
            _eq: params.category,
          },
        },
        fields: [
          'title',
          'slug',
          'color',
          'headline',

          {
            seo: ['*'],
          },
        ],
        limit: 1,
      },
    });

    const postsPromise = useDirectusProxy<Post[]>({
      operation: 'readItems',
      collection: 'posts',
      params: {
        filter: {
          category: {
            slug: {
              _eq: params.category,
            },
          },
        },
        sort: ['date_published'],
        fields: [
          'title',
          'slug',
          'summary',
          'date_published',
          'image',
          'type',
          {
            category: ['title', 'slug', 'color'],
            author: ['image', 'name', 'job_title'],
          },
        ],
      },
    });

    return Promise.all([categoryPromise, postsPromise]);
  },
  {
    transform: ([category, posts]) => {
      return {
        category: category[0],
        posts,
      };
    },
  },
);

const category = computed(() => unref(data)?.category ?? {});
const posts = computed(() => unref(data)?.posts ?? []);

// Compute metadata here to make it easier to populate all the different SEO tags
const metadata = computed(() => {
  const pageData = unref(category);
  const seoObj = typeof pageData?.seo === 'object' ? (pageData?.seo as import('~/types').SEO) : null;
  const imageId = safeFileId(seoObj?.og_image) ?? safeFileId(globals.value?.og_image);
  console.log('pageData', pageData);
  return {
    title: seoObj?.title ?? pageData?.title ?? undefined,
    description: seoObj?.meta_description ?? stripHTML((pageData as any)?.headline) ?? undefined,
    image: imageId ? fileUrl(imageId) : undefined,
    url: seoObj?.canonical_url ?? useRequestURL()?.href,
    logo: 'https://consus-cms.ridiculousengineering.com/assets/d111bc32-e2be-42fa-9024-4d6496c30f72?width=246&height=68',
  };
});

// Compute metadata here to make it easier to populate all the different SEO tags
// const metadata = computed(() => {
//   const pageData = unref(page);
//   const seo = pageData?.seo as SEO;
//   // Normalize file relations to an id before calling fileUrl
//   const imageId = safeFileId(seo?.og_image) ?? safeFileId(globals?.og_image);
//   return {
//     title: seo?.title ?? pageData?.title ?? undefined,
//     description: (seo?.meta_description ?? pageData?.headline) ? stripHTML(pageData?.headline as string) : undefined,
//     image: imageId ? fileUrl(imageId) : undefined,
//     url: seo?.canonical_url ?? url?.href,
//     logo: 'https://consus-cms.ridiculousengineering.com/assets/d111bc32-e2be-42fa-9024-4d6496c30f72?width=246&height=68',
//   };
// });

//Dynamic OG Images
defineOgImage('Page', {
  title: unref(metadata)?.title,
  description: unref(metadata)?.description,
  imageUrl: unref(metadata)?.image,
  logo: unref(metadata)?.logo,
});

// JSON-LD
useSchemaOrg([
  defineWebPage({
    '@type': 'CollectionPage',
    name: unref(metadata)?.title,
    description: unref(metadata)?.description,
  }),
]);

// Page Title
useHead({
  title: () => unref(metadata)?.title,
  link: [
    {
      rel: 'canonical',
      href: unref(metadata)?.url as string,
    },
    {
      rel: 'icon',
      type: 'image/ico',
      href: '/favicon.ico',
    },
    // Apple iOS devices
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: '/apple-touch-icon.png',
    },
    // Android devices / High-res devices
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
      href: '/android-chrome-192x192.png',
    },
    // Smaller devices
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
      href: '/android-chrome-192x192.png',
    },
    // Larger devices
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '512x512',
      href: '/android-chrome-512x512.png',
    },
  ],
});

useSeoMeta({
  description: '[description]',
  ogTitle: '[og:title]',
  ogDescription: '[og:description]',
  ogImage: '[og:image]',
  ogUrl: '[og:url]',
  twitterTitle: '[twitter:title]',
  twitterDescription: '[twitter:description]',
  twitterImage: '[twitter:image]',
  twitterCard: 'summary',
});

// SEO Meta
useServerSeoMeta({
  title: () => unref(metadata)?.title,
  description: () => unref(metadata)?.description,
  ogTitle: () => unref(metadata)?.title,
  ogDescription: () => unref(metadata)?.description,
});
</script>
<template>
  <BlockContainer>
    <header class="pb-6 border-b-2 border-gray-300 dark:border-gray-700">
      <TypographyTitle>{{ category.title }}</TypographyTitle>
      <TypographyHeadline :content="(category as any).headline" />
    </header>
    <section class="relative items-center w-full py-12 space-y-12">
      <div
        class="relative grid gap-12 pb-12 border-b-2 border-gray-300 md:grid-cols-2 lg:grid-cols-4 dark:border-gray-700"
      >
        <div>
          <TypographyTitle as="p" class="text-gray-700 dark:text-gray-400">Search</TypographyTitle>
          <GlobalSearch :collections="['posts']" class="flex" />
          <TypographyTitle as="p" class="mt-8 text-gray-700 dark:text-gray-400">Categories</TypographyTitle>
          <Categories />
        </div>
        <div class="space-y-4 lg:col-span-3">
          <TypographyTitle as="p">Items for Category: {{ deslugify(params.category as string) }}</TypographyTitle>
          <div class="relative grid grid-cols-2 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <PostCard
              v-for="post in posts"
              :key="post.id"
              :post="post"
              class="col-span-2 pb-6 border-b border-gray-300 dark:border-gray-700"
            />
          </div>
        </div>
      </div>
    </section>
  </BlockContainer>
</template>
